import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  executeCommand,
  setupConsoleSocket,
  unSubscribeSocketTopics,
} from "../../../redux/saga/socket";
import { authActions } from "../../../redux/slice/auth.slice";
import { consoleActions } from "../../../redux/slice/console.slice";
import { devicesActions } from "../../../redux/slice/devices.slice";
import { commandTypes } from "../../utils";
import { DeviceStatus, TOPICS } from "../../utils/enums";
import { networkDeviceActions } from "../../../redux/slice/networkDevice.slice";
import { findDeviceById } from "./helper";
import styles from "./console.module.scss";
import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";

const useQuery = () => new URLSearchParams(useLocation().search);

const MyConsole = () => {
  const query = useQuery();
  const id = query.get("id");
  const dispatch = useDispatch();
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [currentDirectory, setCurrentDirectory] = useState("");
  const [pendingCommandText, setPendingCommandText] = useState("");
  const [shellMode, setShellMode] = useState("powershell");

  const terminalContainerRef = useRef(null);
  const terminalInstanceRef = useRef(null);
  const fitAddonRef = useRef(null);
  const endOfConsoleRef = useRef(null);

  const devices = useSelector(devicesActions.selectors.getDevices);
  const deviceNetworks = useSelector(
    networkDeviceActions.selectors.getNetworkDevices
  );
  const consoleResponse = useSelector(
    consoleActions.selectors.getConsoleResponse
  );
  const selectedDevice = useSelector(
    devicesActions.selectors.getSelectedDevice
  );
  const currentUser = useSelector(authActions.selectors.getCurrentUser);
  const selectedDevicesAll = useSelector(
    devicesActions.selectors.getSelectedDevicesAll
  );
  const isCommandProcessing = useSelector(
    consoleActions.selectors.getConsoleProcessing
  );

  const getSingleDevice = useCallback(() => {
    if (id === "multi") {
      return null;
    }
    const found = devices?.find((entry) => entry?._id === id);
    if (found) {
      return found;
    }
    return findDeviceById(deviceNetworks, id);
  }, [devices, deviceNetworks, id]);

  useEffect(() => {
    if (id === "multi") {
      dispatch(consoleActions.actions.setConsoleType("multi"));
    } else {
      dispatch(consoleActions.actions.setConsoleType("single"));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentUser && selectedDevice) {
      if (id === "multi") {
        setCurrentDirectory(`${currentUser?.username}@${id}`);
      } else {
        setCurrentDirectory(
          `${currentUser?.username}@${selectedDevice?.deviceName}`
        );
      }
    }
  }, [currentUser, selectedDevice, id]);

  useEffect(() => {
    if (id === "multi" || !terminalContainerRef.current) {
      return;
    }
    const terminal = new Terminal({
      convertEol: true,
      rendererType: "canvas",
      fontFamily: "Fira Code, Menlo, Consolas, monospace",
      theme: {
        background: "#0b0f16",
        foreground: "#f4f7ff",
      },
      cursorStyle: "bar",
      cursorBlink: true,
      scrollback: 1000,
    });
    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(terminalContainerRef.current);
    fitAddon.fit();
    terminalInstanceRef.current = terminal;
    fitAddonRef.current = fitAddon;

    const handleResize = () => fitAddon.fit();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      terminal.dispose();
      terminalInstanceRef.current = null;
      fitAddonRef.current = null;
    };
  }, [id]);

  useEffect(() => {
    if (
      !consoleResponse ||
      consoleResponse.result === undefined ||
      consoleResponse.result === null
    ) {
      return;
    }
    if (pendingCommandText === consoleResponse.result) {
      return;
    }

    const response = { ...consoleResponse, type: "result" };
    if (id === "multi") {
      setHistory((prevHistory) => {
        const commandEntry = pendingCommandText
          ? {
              status: true,
              result: `${currentDirectory}> ${pendingCommandText}`,
              type: "command",
            }
          : null;
        return [
          ...prevHistory,
          ...(commandEntry ? [commandEntry] : []),
          response,
        ];
      });
    } else {
      terminalInstanceRef.current?.write(`${response.result ?? ""}\r\n`);
    }

    setPendingCommandText("");
    dispatch(consoleActions.actions.setConsoleProcessing(false));
    dispatch(consoleActions.actions.setConsoleResponse([]));
  }, [consoleResponse, currentDirectory, dispatch, id, pendingCommandText]);

  useEffect(() => {
    if (id !== "multi") {
      return;
    }
    endOfConsoleRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, id]);

  useEffect(() => {
    setupConsoleSocket(dispatch);
    return () => {
      unSubscribeSocketTopics([TOPICS.EXECUTE_RESPONSE]);
    };
  }, [dispatch]);

  const os =
    id === "multi"
      ? selectedDevicesAll?.[0]?.systemDetails?.os
      : selectedDevice?.systemDetails?.os;
  const shell = os?.toLowerCase().includes("ubuntu")
    ? "bash"
    : os?.toLowerCase().includes("windows")
    ? "powershell"
    : os || "Unknown";
  const isSingleWindows = id !== "multi" && os?.toLowerCase().includes("windows");
  const displayedShell = isSingleWindows
    ? shellMode === "cmd"
      ? "CMD"
      : "PowerShell"
    : shell;

  useEffect(() => {
    if (!isSingleWindows) {
      setShellMode("powershell");
    }
  }, [isSingleWindows]);

  const formatCommand = (command) => {
    if (!isSingleWindows) {
      return command;
    }
    return shellMode === "powershell" ? `powershell ${command}` : command;
  };

  const processCommand = (command) => {
    if (id === "multi") {
      const connectedDevices = selectedDevicesAll?.filter(
        (device) => device?.status === DeviceStatus.connected
      );
      connectedDevices?.forEach((dev) => {
        executeCommand(
          command,
          commandTypes.CONSOLE,
          dev?.uid,
          dev?.systemDetails?.os
        );
      });
      return;
    }
    const targetDevice = getSingleDevice();
    if (!targetDevice) {
      return;
    }
    executeCommand(
      formatCommand(command),
      commandTypes.CONSOLE,
      targetDevice?.uid,
      targetDevice?.systemDetails?.os
    );
  };

  const handleSendExitSignal = useCallback(() => {
    const targetDevice = getSingleDevice();
    if (!targetDevice?.uid) {
      return;
    }
    executeCommand(
      "exit",
      commandTypes.CONSOLE,
      targetDevice?.uid,
      targetDevice?.systemDetails?.os,
      { signal: true }
    );
    terminalInstanceRef.current?.writeln(`${currentDirectory}> exit`);
    dispatch(consoleActions.actions.setConsoleProcessing(false));
    setPendingCommandText("");
  }, [currentDirectory, dispatch, getSingleDevice]);

  const handleRunCommand = () => {
    if (isCommandProcessing) {
      return;
    }
    if (!input) {
      return;
    }
    const sanitizedInput = input.replace(/[\r\n]+/g, "");
    if (!sanitizedInput) {
      setInput("");
      return;
    }
    const trimmed = sanitizedInput.trim();
    if (!trimmed) {
      setInput("");
      return;
    }
    if (trimmed.toLowerCase() === "clear") {
      setHistory([]);
      dispatch(consoleActions.actions.setConsoleProcessing(false));
      setPendingCommandText("");
      if (id !== "multi") {
        terminalInstanceRef.current?.clear();
      }
      setInput("");
      return;
    }

    setPendingCommandText(sanitizedInput);
    dispatch(consoleActions.actions.setConsoleProcessing(true));
    processCommand(sanitizedInput);
    if (id !== "multi") {
      terminalInstanceRef.current?.writeln(
        `${currentDirectory}> ${sanitizedInput}`
      );
    }
    setInput("");
  };

  const handleKeyPress = (event) => {
    if (event.ctrlKey && event.key === "c") {
      setHistory([]);
      setInput("");
      window.close();
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      handleRunCommand();
    }
  };

  const getDeviceId = (devicesList, uid) => {
    const device = devicesList?.find((dev) => dev?.uid === uid);
    return device?.deviceName;
  };

  const singleDeviceTarget = getSingleDevice();
  const canSendExitSignal = Boolean(singleDeviceTarget?.uid);
  return (
    <div className={styles.consoleContainer}>
      <div className={styles.console}>
        <div className={styles.consoleTop}>
          <p>
            {id === "multi" && `Selected Devices : ${selectedDevicesAll?.length}`}
          </p>
          <p>Shell : {displayedShell}</p>
          <p>
            OS :
            {id === "multi"
              ? selectedDevicesAll?.[0]?.systemDetails?.os
              : selectedDevice?.systemDetails?.os}
          </p>
        </div>
        <div className={styles.consoleOutput}>
          <div className={styles.outputBody}>
            {id === "multi" ? (
              <div className={styles.historyList}>
                {history.map((line, index) => (
                  <pre
                    key={`${line?.uid ?? "r"}-${index}`}
                    style={{ color: line?.status ? "white" : "red" }}
                  >
                    {line?.type === "result" ? (
                      <>
                        <p>
                          {`Device Name: ${getDeviceId(devices, line?.uid)}`}
                        </p>
                        <br />
                        {line?.result}
                      </>
                    ) : (
                      line?.result
                    )}
                  </pre>
                ))}
                <div ref={endOfConsoleRef}></div>
              </div>
            ) : (
              <div
                ref={terminalContainerRef}
                className={styles.terminalWindow}
              ></div>
            )}
          </div>
          {isCommandProcessing && pendingCommandText && (
            <div className={styles.pendingCommandLine}>
              <span className={styles.commandLoader}></span>
              <span>{`${currentDirectory}> ${pendingCommandText}`}</span>
            </div>
          )}
        </div>
        <div className={styles.consoleInput}>
          {isSingleWindows && (
            <select
              value={shellMode}
              onChange={(event) => setShellMode(event.target.value)}
              className={styles.shellSelector}
            >
              <option value="powershell">PowerShell</option>
              <option value="cmd">CMD</option>
            </select>
          )}
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            className={styles.inputField}
            placeholder="Type a command and press Enter"
            disabled={isCommandProcessing}
          />
          <button
            type="button"
            onClick={handleRunCommand}
            className={styles.runButton}
            disabled={isCommandProcessing || !input.trim()}
          >
            Run Command
          </button>
          <button
            type="button"
            className={styles.exitSignalButton}
            onClick={handleSendExitSignal}
            disabled={!canSendExitSignal}
            title={
              canSendExitSignal
                ? "Send exit to stop running command"
                : "Exit signal not available"
            }
          >
            Exit
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyConsole;
